import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PhoneChannelPill from './PhoneChannelPill';

function fmtDate(input, locale) {
  if (!input) return '-';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

function MobileCustomerCard({ u, onDeleteUser, deletingUserId }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = String(i18n.language || 'es').toLowerCase().startsWith('es') ? 'es-ES' : 'en-GB';
  const isFollowUp = Boolean(u.follow_up || u.followUp);

  return (
    <article className="adv-modern-card adv-animate-fade-in flex flex-col mb-4 mx-1">
      {isFollowUp && <div className="adv-card-accent bg-amber-400" />}
      <div className="p-5">
        <div className="flex justify-between items-start mb-4">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => navigate(`/customer/${u.id}`, { state: { user: u } })}
              className="text-base font-bold text-slate-900 text-left hover:text-emerald-600 transition-colors block truncate pr-4"
            >
              {u.name || t('advisorPortal.dirUserNumber', { id: u.id })}
            </button>
            {u.family_name && (
              <div className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-400 font-medium truncate">
                <i className="ri-group-line text-slate-300" />
                {t('advisorPortal.dirFamilyLine', { name: u.family_name })}
              </div>
            )}
          </div>
          {isFollowUp && (
            <span className="shrink-0 flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-wider border border-amber-100">
              {t('advisorPortal.followUpBadge')}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-y-4 gap-x-2 border-t border-slate-50 pt-4 mb-5">
          <div className="min-w-0">
            <span className="block text-[10px] text-slate-400 font-medium uppercase tracking-widest mb-1">{t('advisorPortal.dirHeaderContact')}</span>
            <p className="text-xs text-slate-700 font-medium truncate flex items-center gap-1.5">
              <i className="ri-phone-line text-slate-300" /> {u.phone || '-'}
              <PhoneChannelPill channel={u.phone_signup_channel} />
            </p>
            <p className="text-xs text-slate-500 truncate flex items-center gap-1.5 mt-1">
              <i className="ri-mail-line text-slate-300" /> {u.email || '-'}
            </p>
          </div>
          <div className="min-w-0 text-right">
            <span className="block text-[10px] text-slate-400 font-medium uppercase tracking-widest mb-1">{t('advisorPortal.dirHeaderLastActivity')}</span>
            <p className="text-xs text-slate-700 font-medium">
              {fmtDate(u.last_activity_at || u.lastActivityAt, dateLocale)}
            </p>
            <span className="block text-[10px] text-slate-400 font-medium uppercase tracking-widest mt-2 mb-1">{t('advisorPortal.dirHeaderAddedOn')}</span>
            <p className="text-xs text-slate-500">
              {fmtDate(u.created_at, dateLocale)}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 h-12 flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 active:scale-[0.98] transition-all shadow-md shadow-slate-200"
            onClick={() => navigate(`/customer/${u.id}`, { state: { user: u } })}
          >
            <i className="ri-external-link-line" /> {t('advisorPortal.dirOpen')}
          </button>
          <button
            type="button"
            className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all active:scale-[0.98] ${deletingUserId === u.id
              ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
              : 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100'
              }`}
            onClick={() => onDeleteUser?.(u)}
            disabled={deletingUserId === u.id}
            title={t('advisorPortal.dirDelete')}
          >
            {deletingUserId === u.id ? (
              <i className="ri-loader-4-line animate-spin text-lg" />
            ) : (
              <i className="ri-delete-bin-line text-lg" />
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function AdvisorCustomerDirectoryTable({ users, onDeleteUser, deletingUserId = null }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = String(i18n.language || 'es').toLowerCase().startsWith('es') ? 'es-ES' : 'en-GB';

  const followUpBadge = (u) => {
    const flag = Boolean(u.follow_up || u.followUp);
    if (!flag) return <span className="text-xs text-slate-300">-</span>;
    return <span className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">{t('advisorPortal.followUpBadge')}</span>;
  };

  /* Renewal Due column - re-enable with directory filter + col below
  const renewalBadge = (renewalDue) => {
    if (!renewalDue) return null;
    // renewalDue can be boolean in our data, but can also be a date string.
    const due = typeof renewalDue === 'string' || typeof renewalDue === 'number' ? new Date(renewalDue) : null;
    if (due && !Number.isNaN(due.getTime())) {
      const now = new Date();
      const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return { label: 'Overdue', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
      if (diffDays <= 90) return { label: `Due in ${diffDays}d`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
      return { label: due.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }), cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    }
    return { label: 'Renewal due', cls: 'bg-slate-100 text-slate-500 border-slate-200' };
  };
  */

  if (!users.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-16 text-center text-slate-400 text-sm">
        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 opacity-40">
          <i className="ri-search-line text-3xl" />
        </div>
        {t('advisorPortal.dirNoCustomers')}
      </div>
    );
  }
  return (
    <>
      <div className="space-y-4 sm:hidden pb-10">
        {users.map((u) => (
          <MobileCustomerCard
            key={u.id}
            u={u}
            onDeleteUser={onDeleteUser}
            deletingUserId={deletingUserId}
          />
        ))}
      </div>

      <div className="hidden w-full max-w-full min-w-0 overflow-x-auto overscroll-x-contain touch-pan-x rounded-xl border border-slate-200 sm:block">
        <table className="w-full min-w-[850px]" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '190px' }} />
            <col style={{ width: '220px' }} />
            <col style={{ width: '100px' }} />
            {/* <col style={{ width: '110px' }} /> Renewal Due */}
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '120px' }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="pl-3 pr-1 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.dirHeaderCustomer')}</th>
              <th className="pl-1 pr-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.dirHeaderContact')}</th>
              <th className="pl-2 pr-2 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{t('advisorPortal.dirHeaderFollowUp')}</th>
              {/* <th className="pl-2 pr-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Renewal Due</th> */}
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{t('advisorPortal.dirHeaderAddedOn')}</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{t('advisorPortal.dirHeaderLastActivity')}</th>
              <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide" />
            </tr>
          </thead>
          <tbody>
            {users.map((u, idx) => (
              <tr key={u.id} className={`border-b border-slate-100 transition-colors hover:bg-slate-50/80 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                <td className="pl-3 pr-1 py-3 overflow-hidden">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-medium text-slate-800 break-words line-clamp-2">{u.name || t('advisorPortal.dirUserNumber', { id: u.id })}</p>
                    </div>
                    {u.family_name ? (
                      <p className="text-[11px] text-slate-400 truncate leading-none">
                        {t('advisorPortal.dirFamilyLine', { name: u.family_name })}
                      </p>
                    ) : null}
                  </div>
                </td>
                <td className="pl-1 pr-2 py-3 overflow-hidden">
                  <p className="text-sm text-slate-700 flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0 break-words">
                    <span>{u.phone || '-'}</span>
                    <PhoneChannelPill channel={u.phone_signup_channel} />
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 break-all">{u.email || '-'}</p>
                </td>
                <td className="pl-2 pr-2 py-3">
                  {followUpBadge(u)}
                </td>
                {/* Renewal Due cell - restore with renewalBadge + th/col above
              <td className="pl-2 pr-3 py-3">
                {(() => {
                  const r = renewalBadge(u.renewal_due || u.renewalDue);
                  if (!r) return <span className="text-xs text-slate-300">-</span>;
                  return (
                    <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${r.cls}`}>
                      {r.label}
                    </span>
                  );
                })()}
              </td>
              */}
                <td className="px-3 py-3">
                  <span className="text-sm text-slate-600 whitespace-nowrap">{fmtDate(u.created_at, dateLocale)}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-sm text-slate-600 whitespace-nowrap">
                    {fmtDate(u.last_activity_at || u.lastActivityAt, dateLocale)}
                  </span>
                </td>
                <td className="px-2 py-3">
                  <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end sm:flex-wrap sm:gap-1">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-white bg-slate-700 hover:bg-slate-800 px-2 py-2.5 sm:py-1.5 rounded-md transition-colors cursor-pointer min-h-[44px] sm:min-h-0 touch-manipulation"
                      onClick={() => navigate(`/customer/${u.id}`, { state: { user: u } })}
                    >
                      {t('advisorPortal.dirOpen')} <i className="ri-arrow-right-s-line text-xs shrink-0" />
                    </button>
                    <button
                      type="button"
                      className={`inline-flex items-center justify-center gap-1 text-xs font-medium px-2.5 py-2.5 sm:py-1.5 rounded-md transition-colors min-h-[44px] sm:min-h-0 touch-manipulation ${deletingUserId === u.id
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'text-rose-600 border border-rose-200 hover:bg-rose-50 cursor-pointer'
                        }`}
                      onClick={() => onDeleteUser?.(u)}
                      disabled={deletingUserId === u.id}
                    >
                      {deletingUserId === u.id ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xs" />
                          {t('advisorPortal.dirDeleting')}
                        </>
                      ) : (
                        <>
                          <i className="ri-delete-bin-line text-xs" />
                          {t('advisorPortal.dirDelete')}
                        </>
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

